// MailboxPairAllowlistFromAlertModal — quick-action from any mailbox-pair-allowlist
// -enabled alert row (rule-008 anomalous-access, rule-014 non-owner, …). Writes
// under the alert's own ruleId (v1.70.0).
// Lists every distinct (target_mailbox, actor) pair detected in the alert's
// aggregation source events as a checkbox grid. The heuristic prefill pair is
// pre-checked. Per-mailbox "Any actor (*)" row offers one-click escalation to
// the shared-team-mailbox shape. Apply submits all checked pairs sequentially.
//
// Plan: Plans/2026-05-26_Mailbox-Pair-Allowlist-Portal-Plan-v2.md
//       §"Frontend — AlertsPage quick-action".

import React, { useMemo, useState } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useSourceEvents } from '../sourceEvents/useSourceEvents';
import { parseAggregationFields } from '../sourceEvents/aggregationHelpers';
import { addMailboxPairAllowlistEntry } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Badge from '../ui/Badge';
import { actorTypeTone } from '../ui/badgeTones';

const SID_RE = /^S-\d-\d/;

function actorTypeLabel(t) {
  switch (t) {
    case 'upn':      return 'UPN';
    case 'app_id':   return 'App ID';
    case 'app_name': return 'App Name';
    case '*':        return 'Any (*)';
    default:         return t || '—';
  }
}

// Build the list of distinct (target_mailbox, actor) pairs from the source-events blob.
// Each unique mailbox additionally gets an "Any actor (*)" row at the end (the
// handoff's "typical shape" for shared service-team mailboxes).
//
// `alert` is the full alert envelope — used to surface row-level
// `application_id` / `app_display_name` as preferred candidates per
// 2026-05-26_App-Actor-Display-Followup-Portal-Handoff.md. Those two columns
// are populated by the detection pipeline's resolve_app_identity() hook and
// are the friendly identity for app-only Graph access (Druva, Veeam, etc.)
// that surfaces with a PUID-shaped UserId and no event-level app fields.
function buildPairCandidates(sourceEventsData, aggregation, alert) {
  if (!sourceEventsData?.source_events) return [];
  const delegateUpn = aggregation?.delegate_upn || null;
  const rowAppDisplayName = (alert?.appDisplayName || '').trim().toLowerCase();
  const rowApplicationId  = (alert?.applicationId  || '').trim().toLowerCase().replace(/[{}]/g, '');

  // Group events by (target, actor key).
  const byMailbox = new Map(); // mailbox -> Map(actorKey -> { actorType, actorValue, events, displayActor })
  for (const se of sourceEventsData.source_events) {
    const owner = se.MailboxOwnerUPN || se.MailboxOwnerSid;
    if (!owner) continue;

    // Pick the most-likely actor identity for this source event.
    let actorType = null;
    let actorValue = null;
    let displayActor = null;

    const rawUser = se.UserId || se.UserPrincipalName;
    if (rawUser && SID_RE.test(rawUser) && delegateUpn) {
      // SID + pipeline-resolved delegate UPN -> use the UPN.
      actorType = 'upn';
      actorValue = String(delegateUpn).toLowerCase();
      displayActor = `delegate · ${actorValue}`;
    } else if (rawUser && !SID_RE.test(rawUser)) {
      actorType = 'upn';
      actorValue = String(rawUser).toLowerCase();
      displayActor = actorValue;
    } else if (se.ClientAppId || se.AppId) {
      actorType = 'app_id';
      actorValue = String(se.ClientAppId || se.AppId).toLowerCase().replace(/[{}]/g, '');
      displayActor = `app_id · ${actorValue.substring(0, 8)}…`;
    } else if (se.AppDisplayName) {
      actorType = 'app_name';
      actorValue = String(se.AppDisplayName).toLowerCase();
      displayActor = `app_name · ${se.AppDisplayName}`;
    } else if (rawUser) {
      // Service-SID-only (per pipeline handoff v3, 2026-05-26 — emit actor_type='service_sid'
      // with the SID lowercased verbatim; matches against $.UserId on the source event).
      actorType = 'service_sid';
      actorValue = String(rawUser).toLowerCase();
      displayActor = `service-SID · ${rawUser}`;
    } else {
      continue;
    }

    const mailbox = String(owner).toLowerCase();
    if (!byMailbox.has(mailbox)) byMailbox.set(mailbox, new Map());
    const inner = byMailbox.get(mailbox);
    const key = `${actorType}|${actorValue}`;
    if (!inner.has(key)) {
      inner.set(key, { actorType, actorValue, events: 0, displayActor });
    }
    inner.get(key).events += 1;
  }

  // Flatten to candidates list. Per mailbox, prepend alert-row app identities
  // (when present) ahead of event-level identities, then append the "Any actor
  // (*)" row at the bottom.
  const candidates = [];
  const seenKeysByMailbox = new Map(); // mailbox -> Set<actorType|actorValue>
  for (const mailbox of byMailbox.keys()) {
    seenKeysByMailbox.set(mailbox, new Set());
  }

  // Pass 1 — alert-row app identities (rule-008 single-accessor → applies to
  // every mailbox in the aggregation). Prepended so they outrank event-level
  // service_sid fallbacks in prefill ranking.
  for (const mailbox of byMailbox.keys()) {
    const seen = seenKeysByMailbox.get(mailbox);
    if (rowAppDisplayName) {
      const key = `app_name|${rowAppDisplayName}`;
      candidates.push({
        id: `${mailbox}::app_name::${rowAppDisplayName}`,
        targetMailboxUpn: mailbox,
        actorType: 'app_name',
        actorValue: rowAppDisplayName,
        events: 0,
        displayActor: `app_name · ${alert.appDisplayName}`,
        isWildcardAny: false,
        isAlertRowIdentity: true,
      });
      seen.add(key);
    }
    if (rowApplicationId) {
      const key = `app_id|${rowApplicationId}`;
      candidates.push({
        id: `${mailbox}::app_id::${rowApplicationId}`,
        targetMailboxUpn: mailbox,
        actorType: 'app_id',
        actorValue: rowApplicationId,
        events: 0,
        displayActor: `app_id · ${rowApplicationId.substring(0, 8)}…`,
        isWildcardAny: false,
        isAlertRowIdentity: true,
      });
      seen.add(key);
    }
  }

  // Pass 2 — event-level identities (existing logic). Deduplicate against
  // pass-1 emissions.
  for (const [mailbox, actors] of byMailbox.entries()) {
    const seen = seenKeysByMailbox.get(mailbox);
    for (const [, info] of actors.entries()) {
      const key = `${info.actorType}|${info.actorValue}`;
      if (seen.has(key)) continue;
      candidates.push({
        id: `${mailbox}::${info.actorType}::${info.actorValue}`,
        targetMailboxUpn: mailbox,
        actorType: info.actorType,
        actorValue: info.actorValue,
        events: info.events,
        displayActor: info.displayActor,
        isWildcardAny: false,
      });
      seen.add(key);
    }
  }

  // Pass 3 — per-mailbox "Any actor (*)" shared-mailbox escalation row.
  for (const mailbox of byMailbox.keys()) {
    candidates.push({
      id: `${mailbox}::*::*`,
      targetMailboxUpn: mailbox,
      actorType: '*',
      actorValue: '*',
      events: 0,
      displayActor: 'Any actor (*)',
      isWildcardAny: true,
    });
  }

  return candidates;
}

// Heuristic prefill pair. Precedence:
//   1. Alert-row `app_name` (handoff: friendliest identity for app-only access)
//   2. Alert-row `app_id`   (GUID fallback when the registry hasn't learned the name)
//   3. Pipeline-resolved `delegate_upn` (real human delegate)
//   4. First non-wildcard candidate
function pickPrefillId(candidates, aggregation) {
  if (!candidates.length) return null;

  const rowAppName = candidates.find((c) => c.isAlertRowIdentity && c.actorType === 'app_name');
  if (rowAppName) return rowAppName.id;

  const rowAppId = candidates.find((c) => c.isAlertRowIdentity && c.actorType === 'app_id');
  if (rowAppId) return rowAppId.id;

  const delegateUpn = aggregation?.delegate_upn ? String(aggregation.delegate_upn).toLowerCase() : null;
  if (delegateUpn) {
    const match = candidates.find((c) => c.actorType === 'upn' && c.actorValue === delegateUpn);
    if (match) return match.id;
  }

  const first = candidates.find((c) => !c.isWildcardAny);
  return first?.id || candidates[0].id;
}

export default function MailboxPairAllowlistFromAlertModal({ alert, onClose, onApplied }) {
  const { user } = useAuth();
  const { data: sourceEventsData, loading: blobLoading, error: blobError } = useSourceEvents(
    alert?.sourceEventsBlobPath,
    alert?.tenantId,
    alert?.alertId,
  );

  const aggregation = useMemo(() => parseAggregationFields(alert?.rawEventJson), [alert]);
  const candidates = useMemo(
    () => buildPairCandidates(sourceEventsData, aggregation, alert),
    [sourceEventsData, aggregation, alert],
  );

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [prefillApplied, setPrefillApplied] = useState(false);

  // Apply the prefill heuristic the first time candidates arrive.
  React.useEffect(() => {
    if (!prefillApplied && candidates.length > 0) {
      const prefill = pickPrefillId(candidates, aggregation);
      if (prefill) {
        setSelectedIds(new Set([prefill]));
      }
      setPrefillApplied(true);
    }
  }, [candidates, aggregation, prefillApplied]);

  const [notes, setNotes] = useState(() => {
    const upn = user?.userPrincipalName || user?.username || '';
    return `Allowlisted from alert ${alert?.alertId || ''} on ${new Date().toISOString().slice(0, 10)}${upn ? ` by ${upn}` : ''}.`;
  });

  const [applying, setApplying] = useState(false);
  const [applyProgress, setApplyProgress] = useState({ done: 0, total: 0 });
  const [applyError, setApplyError] = useState(null);
  const [appliedSummary, setAppliedSummary] = useState(null);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(candidates.map((c) => c.id)));
  const selectNone = () => setSelectedIds(new Set());

  const selectedPairs = useMemo(
    () => candidates.filter((c) => selectedIds.has(c.id)),
    [candidates, selectedIds],
  );

  const previewLine = useMemo(() => {
    if (selectedPairs.length === 0) return 'Pick at least one pair to allowlist.';
    if (selectedPairs.length === 1) {
      const p = selectedPairs[0];
      const target = p.targetMailboxUpn === '*' ? 'any mailbox' : p.targetMailboxUpn;
      if (p.actorType === '*') return `Any actor may access ${target}.`;
      return `Only ${actorTypeLabel(p.actorType)} ${p.actorValue} may access ${target}.`;
    }
    return `${selectedPairs.length} pairs will be added. Pipeline pickup within ~90 s (or ~150 s on retry).`;
  }, [selectedPairs]);

  const handleApply = async () => {
    if (selectedPairs.length === 0) return;
    setApplying(true);
    setApplyError(null);
    setApplyProgress({ done: 0, total: selectedPairs.length });
    let anyPending = false;
    for (let i = 0; i < selectedPairs.length; i++) {
      const p = selectedPairs[i];
      try {
        const res = await addMailboxPairAllowlistEntry({
          ruleId:           alert.ruleId,
          tenantId:         alert.tenantId,
          targetMailboxUpn: p.targetMailboxUpn,
          actorType:        p.actorType,
          actorValue:       p.actorValue,
          notes:            notes.trim(),
        });
        if (res?.pendingSync) anyPending = true;
      } catch (err) {
        setApplyError(`Failed on pair ${i + 1} of ${selectedPairs.length} (${p.targetMailboxUpn} / ${p.actorType} / ${p.actorValue}): ${err.message || err}`);
        setApplying(false);
        return;
      }
      setApplyProgress({ done: i + 1, total: selectedPairs.length });
    }
    setApplying(false);
    setAppliedSummary({ count: selectedPairs.length, pendingSync: anyPending });
    if (onApplied) onApplied();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Allowlist mailbox pair(s) from alert</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Alert <span className="font-mono">{alert.alertId}</span> · Tenant <span className="font-mono">{alert.tenantId}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Loading / errors */}
          {blobLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-gray-500 text-sm">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Loading source events…
            </div>
          )}
          {blobError && (
            <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>{blobError}</span>
            </div>
          )}
          {!blobLoading && !blobError && candidates.length === 0 && (
            <div className="p-3 bg-amber-900/30 border border-amber-700/50 rounded text-amber-300 text-sm flex items-start gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                No mailbox-pair candidates detected. This alert may not be an aggregation row, or its source events
                lack mailbox-owner identity. You can still add an entry manually from the admin page.
              </span>
            </div>
          )}

          {/* Pair candidates table */}
          {!blobLoading && candidates.length > 0 && (
            <>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {candidates.filter((c) => !c.isWildcardAny).length} distinct (mailbox, actor) pair{candidates.length !== 1 ? 's' : ''} detected
                  {' '}· {candidates.filter((c) => c.isWildcardAny).length} "Any actor" escalation row{candidates.filter((c) => c.isWildcardAny).length !== 1 ? 's' : ''}
                </span>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="underline hover:no-underline">Select all</button>
                  <button onClick={selectNone} className="underline hover:no-underline">Clear</button>
                </div>
              </div>
              <div className="border border-gray-800 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-950">
                    <tr className="border-b border-gray-800">
                      <th className="w-10 px-3 py-2"></th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">Mailbox</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">Actor</th>
                      <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">Events</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {candidates.map((c) => (
                      <tr key={c.id} className={c.isWildcardAny ? 'bg-amber-950/20' : ''}>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={() => toggle(c.id)}
                            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-300 truncate">{c.targetMailboxUpn}</td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-2">
                            <Badge tone={actorTypeTone(c.actorType)}>{actorTypeLabel(c.actorType)}</Badge>
                            <span className={c.isWildcardAny ? 'text-amber-300' : 'font-mono text-gray-300'}>
                              {c.isWildcardAny ? 'Any actor (*)' : c.displayActor}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-gray-500">{c.events || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes (applied to every selected pair)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field"
              maxLength={2000}
            />
          </div>

          {/* Preview */}
          <p className="text-xs text-blue-300/80">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Preview:</span>{' '}
            <span className="text-gray-300">{previewLine}</span>
          </p>

          {/* Apply state */}
          {applying && (
            <div className="p-3 bg-blue-900/20 border border-blue-700/40 rounded text-blue-300 text-sm flex items-center gap-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              <span>Applying {applyProgress.done} of {applyProgress.total}…</span>
            </div>
          )}
          {applyError && (
            <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-start gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{applyError}</span>
            </div>
          )}
          {appliedSummary && (
            <div className="p-3 bg-green-900/30 border border-green-700/40 rounded text-green-300 text-sm flex items-center gap-2">
              <CheckIcon className="h-4 w-4" />
              <span>
                Applied {appliedSummary.count} pair{appliedSummary.count !== 1 ? 's' : ''}.
                {appliedSummary.pendingSync ? ' Pipeline may take up to ~150 s to pick up these changes (sentinel bump pending).' : ' Pipeline pickup within ~90 s.'}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800 bg-gray-950/40">
          <button onClick={onClose} className="btn-secondary text-sm">
            {appliedSummary ? 'Close' : 'Cancel'}
          </button>
          {!appliedSummary && (
            <button
              onClick={handleApply}
              disabled={applying || selectedPairs.length === 0}
              className="btn-primary text-sm flex items-center gap-2"
            >
              {applying ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
              Apply {selectedPairs.length > 0 ? `(${selectedPairs.length})` : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
